import fetch from 'isomorphic-fetch';
import moment from 'moment';
import { toastsActions } from '@zola-helpers/client';

import * as ActionType from './types/ChecklistActionTypes';
import ApiService from '../util/api';
import toggleInArray from '../util/toggleInArray';
import { trackOptimizelyEvent } from '../util/trackingHelper';

// TODO: MOVE THIS AND USE FOR ALL REQUESTS!!!!
function handleErrors(response) {
  if (!response.ok) {
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
  return response;
}

function requestChecklist() {
  return {
    type: ActionType.REQUEST_CHECKLIST,
  };
}

function receiveChecklist(json) {
  return {
    type: ActionType.RECEIVE_CHECKLIST,
    payload: {
      checklist: json,
    },
  };
}

function requestStats() {
  return {
    type: ActionType.REQUEST_STATS,
  };
}

function receiveStats(json) {
  return {
    type: ActionType.RECEIVE_STATS,
    payload: {
      globalStats: json.global_stats,
    },
  };
}

function updateChecklist() {
  return {
    type: ActionType.UPDATE_CHECKLIST,
  };
}

function requestTemplates() {
  return {
    type: ActionType.REQUEST_TEMPLATES,
  };
}

function receiveTemplates(json) {
  return {
    type: ActionType.RECEIVE_TEMPLATES,
    payload: {
      templates: json,
    },
  };
}

export function addTask() {
  return {
    type: ActionType.ADD_TASK,
  };
}

function updateTask() {
  return {
    type: ActionType.UPDATE_TASK,
  };
}

function removeTask(taskId) {
  return {
    type: ActionType.REMOVE_TASK,
    payload: {
      id: taskId,
    },
  };
}

export function receiveTask(json) {
  return {
    type: ActionType.RECEIVE_TASK,
    payload: {
      task: json,
    },
  };
}

export function resetChecklist() {
  return {
    type: ActionType.RESET_CHECKLIST,
  };
}

export function incOnboardStep() {
  return {
    type: ActionType.INC_ONBOARD_STEP,
  };
}

export function decOnboardStep() {
  return {
    type: ActionType.DEC_ONBOARD_STEP,
  };
}

export function toggleOnboard(show) {
  return {
    type: ActionType.TOGGLE_ONBOARD,
    payload: {
      show,
    },
  };
}

export function selectTask(taskId) {
  return {
    type: ActionType.SELECT_TASK,
    payload: {
      id: taskId,
    },
  };
}

export function unselectTask() {
  return {
    type: ActionType.UNSELECT_TASK,
  };
}

export function toggleTemplates(templateId) {
  return (dispatch, getState) => {
    const { selectedTemplates } = getState().checklist.templates;
    return dispatch({
      type: ActionType.TOGGLE_TEMPLATES,
      payload: toggleInArray(templateId, selectedTemplates),
    });
  };
}

export function fetchChecklist() {
  return dispatch => {
    dispatch(requestChecklist());

    return fetch('/web-api/v1/checklist', {
      credentials: 'same-origin',
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(json => dispatch(receiveChecklist(json)));
  };
}

export function fetchStats() {
  return dispatch => {
    dispatch(requestStats());

    return fetch('/web-api/v1/checklist', {
      credentials: 'same-origin',
    })
      .then(response => response.json())
      .then(json => dispatch(receiveStats(json)));
  };
}

export function editChecklist(updateChecklistRequest) {
  return dispatch => {
    dispatch(updateChecklist());

    // Normalize wedding date
    const weddingDateObj = moment.utc(
      updateChecklistRequest.wedding_date || updateChecklistRequest.wedding_month_year_date
    );
    const dateValid = weddingDateObj.isValid();
    let weddingDateJson;
    if (dateValid) {
      weddingDateJson = updateChecklistRequest.wedding_date
        ? { wedding_date: weddingDateObj.format('YYYY-MM-DD') }
        : { wedding_month_year_date: weddingDateObj.format('YYYY-MM-DD') };
    } else {
      weddingDateJson = { wedding_date: null };
    }

    const finalRequest = {
      ...updateChecklistRequest,
      ...weddingDateJson,
    };

    return ApiService.post('/web-api/v1/checklist', finalRequest)
      .then(json => dispatch(receiveChecklist(json)))
      .catch(() =>
        dispatch(
          toastsActions.negative({ headline: 'There was an error creating your checklist.' })
        )
      );
  };
}

export function fetchTemplates() {
  return dispatch => {
    dispatch(requestTemplates());

    return fetch('/web-api/v1/checklist/template', {
      credentials: 'same-origin',
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(json => dispatch(receiveTemplates(json)));
  };
}

export function createTask(createTaskRequest) {
  // console.log(createTaskRequest);
  return dispatch => {
    dispatch(addTask());

    const finalRequest = { ...createTaskRequest, due_at: moment.utc(createTaskRequest.due_at) };

    return fetch('/web-api/v1/checklist/task', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalRequest),
    })
      .then(response => response.json())
      .then(json => dispatch(receiveTask(json)))
      .then(() => Promise.resolve(dispatch(toastsActions.positive({ headline: 'Task Added' }))));
  };
}

export function editTask(editTaskRequest) {
  return dispatch => {
    dispatch(updateTask());

    const finalRequest = { ...editTaskRequest, due_at: moment.utc(editTaskRequest.due_at) };

    return fetch(`/web-api/v1/checklist/task/id/${finalRequest.id}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalRequest),
    })
      .then(response => response.json())
      .then(json => dispatch(receiveTask(json)))
      .then(() => dispatch(toastsActions.positive({ headline: 'Task Updated' })));
  };
}

export function deleteTask(taskId) {
  return dispatch =>
    fetch(`/web-api/v1/checklist/task/id/${taskId}/delete`, {
      method: 'POST',
      credentials: 'same-origin',
    })
      .then(() => dispatch(removeTask(taskId)))
      .then(() => dispatch(toastsActions.positive({ headline: 'Task Deleted' })));
}

export function toggleCompleted(taskId) {
  return (dispatch, getState) => {
    const request = { ...getState().checklist.tasks.byId[taskId] };
    request.completed = !(request.completed_at !== null);
    if (request.completed) trackOptimizelyEvent('Checklist Task Marked as Complete');
    return dispatch(editTask(request));
  };
}

export const requestTraditions = () => ({
  type: ActionType.REQUEST_TRADITIONS,
});

export const receiveTraditions = json => ({
  type: ActionType.RECEIVE_TRADITIONS,
  payload: json,
});

export const fetchTraditions = () => dispatch => {
  dispatch(requestTraditions());
  return ApiService.get('/web-api/v1/checklist/template/level/2')
    .then(res => dispatch(receiveTraditions({ templates: res })))
    .catch(() =>
      dispatch(toastsActions.negative({ headline: 'Error in getting traditions for onboarding.' }))
    );
};



// WEBPACK FOOTER //
// ./src/actions/ChecklistActions.js